import styled from "styled-components";
import Text from "common/Typography/Text";
import React, { ReactNode, useState } from "react";
import Checkbox from "common/Checkbox/Checkbox";
import Radio from "common/Radio/Radio";
import LinkButton from "common/Buttons/LinkButton";
import { FilterSectionData } from "@shared/features/filters/types";
import IconWithFallback from "Global/IconWithFallback";
import {
  CheckboxFilterType,
  FilterSectionType,
  FilterType,
} from "@shared/types";
import { typography } from "@shared/common/Themes/typography";
import Rate from "common/Rate/Rate";
import InactiveAccountIcon from "features/configuration/common/InactiveAccountIcon";
import { useInvalidAccount } from "@shared/features/settings/hooks";
import { useBottomNavigator } from "utils/hooks/misc";
import LongPress from "common/Gustures/LongPress.tsx";

const CheckboxFiltersSection = ({
  filterType,
  filterSectionType,
  options: propsOptions,
  displayData,
  getIsSelected,
  toggleItem,
  onChange,
}: FilterSectionData<CheckboxFilterType>) => {
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const [hoveredOption, setHoveredOption] = useState<
    string | number | undefined
  >();
  const [showAll, setShowAll] = useState(false);
  const { getInvalidAccountData } = useInvalidAccount();

  const defaultNumToShow =
    filterType === FilterType.AVG_RATING ? 6 : NUM_TO_SHOW;
  const displayShowAll = propsOptions && propsOptions.length > defaultNumToShow;
  const options = propsOptions?.slice(
    0,
    showAll ? propsOptions.length : defaultNumToShow
  );

  const getOptionDisplay = (
    option: string | number,
    showSelectOnly: boolean
  ) => {
    const accountId = displayData.getAccountId?.(option);
    const invalidAccountData = getInvalidAccountData(accountId);
    let optionDisplay: ReactNode;
    if (filterType === FilterType.AVG_RATING) {
      if (option === -1) optionDisplay = <Text>Unrated</Text>;
      else {
        optionDisplay = (
          <Rate
            disabled
            defaultValue={option as number}
            style={{ fontSize: 13 }}
          />
        );
      }
    } else {
      optionDisplay = (
        <>
          <InactiveAccountIcon accountId={accountId}>
            <Text
              $ellipsis={invalidAccountData.isInvalidOrInactive}
              style={{ color: "inherit" }}
            >
              {displayData.getDisplay?.(option) || option}
            </Text>
          </InactiveAccountIcon>
          {displayData.getExtraInfo && (
            <Text type={"secondary"} $size={typography.fontSizes.S}>
              {displayData.getExtraInfo(option)}
            </Text>
          )}
        </>
      );
    }
    return (
      <>
        <FilterNameAndInfoWrapper
          $hasIcon={!!displayData.getIconProps}
          $hasExtraInfo={!!displayData.getExtraInfo}
        >
          {optionDisplay}
        </FilterNameAndInfoWrapper>
        {displayData.getIconProps && !showSelectOnly && (
          <IconWithFallback
            {...displayData.getIconProps(option)}
            iconSize="xxl"
            iconStyle={{
              position: "absolute",
              right: 0,
              borderRadius: 4,
            }}
          />
        )}
      </>
    );
  };

  const onOptionClick = (option: string | number, isLongPress?: boolean) => {
    filterSectionType === FilterSectionType.RADIO || isLongPress
      ? onChange([option] as any)
      : toggleItem(option);
  };

  return (
    <>
      {options?.map((option, index) => {
        const showSelectOnly =
          !isBottomNavigator &&
          SELECT_ONLY_FILTER_TYPES.includes(filterType) &&
          hoveredOption === option;
        const isSelected = getIsSelected(option);

        const optionElement = (
          <FilterItemWrapper
            key={option}
            onClick={() => onOptionClick(option)}
            onMouseEnter={() => setHoveredOption(option)}
            onMouseLeave={() => setHoveredOption(undefined)}
          >
            {filterSectionType === FilterSectionType.CHECKBOX ? (
              <Checkbox checked={isSelected} />
            ) : (
              <Radio checked={isSelected} style={{ margin: 0 }} />
            )}
            <FilterInfoIconWrapper>
              {getOptionDisplay(option, showSelectOnly)}
              {showSelectOnly && (
                <SelectOnlyButton
                  type="link"
                  onClick={(e) => {
                    onChange([option] as any);
                    e.stopPropagation();
                  }}
                >
                  only
                </SelectOnlyButton>
              )}
            </FilterInfoIconWrapper>
          </FilterItemWrapper>
        );
        return (
          <LongPress
            key={index}
            triggerElement={optionElement}
            onLongPress={() => onOptionClick(option, true)}
          />
        );
      })}
      {displayShowAll && (
        <LinkButton
          type="link"
          onClick={() => setShowAll(!showAll)}
          style={{ paddingLeft: 0, marginTop: -8 }}
        >
          {showAll ? "Show less" : "Show more"}
        </LinkButton>
      )}
    </>
  );
};

export default CheckboxFiltersSection;

const NUM_TO_SHOW = 5;
const SELECT_ONLY_FILTER_TYPES = [
  FilterType.EXCHANGES,
  FilterType.CLIENTS,
  FilterType.MULTI_GROUPS,
  FilterType.SOURCE_NAME,
  FilterType.TRADE_TYPE,
  FilterType.SOURCE_TYPE,
  FilterType.SIGNAL_STATUS,
  FilterType.AVG_RATING,
  FilterType.BACKTESTING_STATUS,
  FilterType.STATUS,
];

const FilterInfoIconWrapper = styled.div`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-left: 12px;
  overflow: hidden;
  flex: 1;
`;
const FilterItemWrapper = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  -webkit-user-select: none;
  -ms-user-select: none;
  user-select: none;
`;
const FilterNameAndInfoWrapper = styled.div<{
  $hasIcon?: boolean;
  $hasExtraInfo?: boolean;
}>`
  flex: 1;
  padding: 6px 0;
  ${(p) => {
    if (p.$hasIcon && p.$hasExtraInfo) return "padding: 6px 32px 6px 0";
    else if (p.$hasIcon) return "padding: 8px 32px 8px 0";
  }};
  overflow: hidden;
`;
const SelectOnlyButton = styled(LinkButton)`
  padding: 0;
  font-size: ${(p) => p.theme.typography.fontSizes.S};
  font-weight: ${(p) => p.theme.typography.fontWeights.MEDIUM};
  position: absolute;
  right: 0;
  &:hover > span {
    text-decoration: underline;
  }
`;
